import { useMemo, useState } from "react";
import type { ProductionReferenceData, TerminalAdministration } from "../models";
import { productionGateway } from "../production-gateway";
import { currentDeviceTerminalCode, terminalCodeStorageKey, pendingTerminalCodeStorageKey } from "./terminal-device-identity";
import type { ProductionWorkspaceProps } from "../production-workspace-contract";

export type TerminalPairingOptions = {
  data: TerminalAdministration;
  reference: ProductionReferenceData;
  onSaved: () => Promise<void>;
  notify: ProductionWorkspaceProps["notify"];
  setSaving: (saving: boolean) => void;
};

export function useTerminalPairingWorkspace({ data, reference, onSaved, notify, setSaving }: TerminalPairingOptions) {
  const deviceTerminalCode = useMemo(() => currentDeviceTerminalCode(), []);
  const configuredDeviceTerminal = data.terminals.find((item) => item.terminalCode === deviceTerminalCode);
  const initialTerminalLineId = configuredDeviceTerminal?.lineId ?? reference.lines[0]?.id ?? "";
  const initialTerminalLine = reference.lines.find((item) => item.id === initialTerminalLineId);
  const [enrollThisDevice, setEnrollThisDevice] = useState(true);
  const [terminalCodeValue, setTerminalCodeValue] = useState(deviceTerminalCode);
  const [terminalName, setTerminalName] = useState(configuredDeviceTerminal?.displayName ?? (initialTerminalLine ? `${initialTerminalLine.name} Terminali` : ""));
  const [terminalLineId, setTerminalLineId] = useState(initialTerminalLineId);
  const [terminalEnrollmentCode, setTerminalEnrollmentCode] = useState("");
  const [terminalAutoLogin, setTerminalAutoLogin] = useState(configuredDeviceTerminal?.autoLogin ?? false);
  const [registeredTerminal, setRegisteredTerminal] = useState<{ code: string; lineName: string } | null>(null);
  const pairTerminal = async () => {
    if (!terminalCodeValue.trim() || !terminalName.trim() || !terminalLineId) return;
    setSaving(true);
    setRegisteredTerminal(null);
    try {
      const code = terminalCodeValue.trim().toUpperCase();
      const lineName = reference.lines.find((item) => item.id === terminalLineId)?.name ?? "Seçilen makine";
      const terminal = await productionGateway.pairTerminal(code, { displayName: terminalName.trim(), lineId: terminalLineId, active: true, autoLogin: terminalAutoLogin });
      setTerminalEnrollmentCode(terminal.enrollmentCode);
      if (enrollThisDevice) {
        try {
          await productionGateway.enrollTerminal(code, terminal.enrollmentCode);
          window.localStorage.setItem(terminalCodeStorageKey, code);
          if (terminalAutoLogin) window.localStorage.setItem("anl-production-terminal-autostart", "true");
          else window.localStorage.removeItem("anl-production-terminal-autostart");
          window.localStorage.removeItem(pendingTerminalCodeStorageKey);
          setTerminalEnrollmentCode("");
          setRegisteredTerminal({ code, lineName });
          notify(`Bu tablet ${lineName} terminali olarak kaydedildi.`);
        } catch (enrollmentError) {
          notify(enrollmentError instanceof Error
            ? `Hat eşlemesi kaydedildi fakat bu tablet doğrulanamadı: ${enrollmentError.message}`
            : "Hat eşlemesi kaydedildi fakat bu tablet doğrulanamadı. Aşağıdaki anahtarla tekrar deneyin.");
        }
      } else {
        notify("Terminal–makine eşlemesi kaydedildi; kayıt anahtarı hazır.");
      }
      await onSaved();
    } catch (error) { notify(error instanceof Error ? error.message : "Terminal eşlemesi kaydedilemedi."); }
    finally { setSaving(false); }
  };
  return { deviceTerminalCode, enrollThisDevice, setEnrollThisDevice, terminalCodeValue, setTerminalCodeValue, terminalName, setTerminalName, terminalLineId, setTerminalLineId, terminalEnrollmentCode, setTerminalEnrollmentCode, terminalAutoLogin, setTerminalAutoLogin, registeredTerminal, setRegisteredTerminal, pairTerminal };
}

export type TerminalPairingWorkspace = ReturnType<typeof useTerminalPairingWorkspace>;
